import { createShowcaseProjects, getShowcaseMilestonesForProject, getShowcaseProject, getShowcaseTasksForProject } from '../demo/showcaseCatalog';

/**
 * Base service class that provides common CRUD functionality
 * for all service classes to inherit from.
 */
export abstract class BaseService<T> {
  protected baseUrl: string;

  constructor(baseUrl:  string) {
    this.baseUrl = baseUrl;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log(`BaseService initialized with baseUrl: ${baseUrl}`);
    }
  }

  /**
   * Generic fetch method with error handling - MOCKED for development
   * In development mode, this will return mock data instead of making real API calls
   */
  protected async fetchWithErrorHandling<R>(_url:  string, 
    options?: RequestInit
  ): Promise<R> {
    try {
      const url = _url;
      if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
        console.log(`Mock API call to ${url}`);
      }
      
  // Check if we're in development mode (not production)
  // In Jest and node test environments, treat as non-production to use mocks
  const isProd = false as boolean;
  if (!isProd) {
        try {
          // Simulate a minimal delay for API response (reduced from 100ms to 10ms for better performance)
          await new Promise(resolve => setTimeout(resolve, 10));
          
          const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

          // Return showcase data based on the URL path
          const mockData = this.getMockDataForUrl(normalizedUrl);
          
          // Ensure we have data to return
          if (mockData === undefined || mockData === null || 
              (Array.isArray(mockData) && mockData.length === 0)) {
            console.error(`No mock data available for endpoint: ${normalizedUrl}`);
            throw new Error(`No mock data available for endpoint: ${normalizedUrl}`);
          }
          
          // Check if it's an error response
          if (mockData && typeof mockData === 'object' && 'error' in mockData) {
            throw new Error((mockData as { error: string }).error);
          }
          
          if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
            console.log(`Returning mock data for ${normalizedUrl}:`, mockData);
          }
          return mockData as R;
        } catch (mockError) {
          console.error(`Error generating mock data for ${url}:`, mockError);
          throw new Error(`Failed to fetch project data: ${mockError instanceof Error ? mockError.message : String(mockError)}`);
        }
      }
      
      // In production, make the actual API call
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${_url}:`, error);
      throw error;
    }
  }
  
  /**
   * Returns mock data based on the URL
   */
  protected getMockDataForUrl(_url:  string): unknown {
    // Extract the path from the URL, removing any query parameters
    const url = _url;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log('Full URL for mock data:', url);
    }
    
    // Handle potential missing leading slash
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log('Normalized URL:', normalizedUrl);
    }
    
    // Handle query parameters by splitting them off
    const [basePath, queryParams] = normalizedUrl.split('?');
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log('Base path:', basePath, 'Query params:', queryParams || 'none');
    }
    
    const urlParts = basePath.split('/').filter(part => part !== '');
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log('URL parts (filtered):', urlParts);
    }
    
    // Determine the actual resource being requested based on URL structure
    // With proper filtering, index 0 should be the first meaningful part
    let resource: string | undefined;
    let resourcePath: string | undefined;
    
    // Handle both /api/projects and /projects patterns
    if (urlParts[0] === 'api' && urlParts[1] === 'projects') {
      resource = 'projects';
      resourcePath = urlParts.slice(2).join('/'); // Everything after /api/projects
    } else if (urlParts[0] === 'projects') {
      resource = 'projects';
      resourcePath = urlParts.slice(1).join('/'); // Everything after /projects
    } else {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
        console.warn('Unrecognized URL structure:', normalizedUrl);
      }
    }
    
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log(`Parsed resource: ${resource || 'undefined'}, resource path: ${resourcePath || 'undefined'}`);
    }
    
    // Project mocks are adapters over the canonical showcase catalog.
    if (resource === 'projects') {
      if (resourcePath && resourcePath.length > 0) {
        const [projectId, subresource] = resourcePath.split('/');

        if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
          console.log(`Project ID: ${projectId}, subresource: ${subresource || 'none'}`);
        }

        if (!subresource) return getShowcaseProject(projectId);
        if (subresource === 'tasks') return getShowcaseTasksForProject(projectId);
        if (subresource === 'milestones') return getShowcaseMilestonesForProject(projectId);
        return null;
      }

      return createShowcaseProjects();
    }

    // Default fallback data if we couldn't determine the resource type
    return { message: "No mock data available for this endpoint" };
  }

  /**
   * Generic get method
   */
  protected async get<R>(_path:  string): Promise<R> {
    const path = _path.startsWith('/') ? _path : `/${_path}`;
    const fullUrl = `${this.baseUrl}${path}`;
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'test') {
      console.log(`BaseService.get() called with path: "${path}", constructing full URL: "${fullUrl}"`);
    }
    return this.fetchWithErrorHandling<R>(fullUrl);
  }

  /**
   * Generic post method
   */
  protected async post<R>(_path:  string, data: Record<string, unknown>): Promise<R> {
    const path = _path.startsWith('/') ? _path : `/${_path}`;
    return this.fetchWithErrorHandling<R>(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Generic patch method
   */
  protected async patch<R>(_path:  string, data: Record<string, unknown>): Promise<R> {
    const path = _path.startsWith('/') ? _path : `/${_path}`;
    return this.fetchWithErrorHandling<R>(`${this.baseUrl}${path}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Generic delete method
   */
  protected async delete<R>(_path:  string): Promise<R> {
    const path = _path.startsWith('/') ? _path : `/${_path}`;
    return this.fetchWithErrorHandling<R>(`${this.baseUrl}${path}`, {
      method: 'DELETE',
    });
  }

  /**
   * Create a new entity
   */
  async create(_data:  Partial<T>): Promise<T> {
    return this.post<T>('', _data as unknown as Record<string, unknown>);
  }

  /**
   * Get entity by ID
   */
  async getById(_id:  string): Promise<T> {
    return this.get<T>(`/${_id}`);
  }

  /**
   * Update entity by ID
   */
  async update(_id:  string, _data: Partial<T>): Promise<T> {
    return this.patch<T>(`/${_id}`, _data as unknown as Record<string, unknown>);
  }

  /**
   * Delete entity by ID
   */
  async remove(_id:  string): Promise<boolean> {
    await this.delete<void>(`/${_id}`);
    return true;
  }
}

